import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { UpdateUserOrganizationsData, User } from '@/shared/types/userTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

/** Infinite scroll fetch for users load-more. */
export const useInfiniteUsers = () => {
  return useInfiniteQuery({
    queryKey: ['users-infinite'],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<User>>(
        `/enterprise/v1/users`,
        { params: { page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
  });
};

// Delete User Mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/users/${userId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-deleted'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

export const useUpdateUserAccounts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, userData }: { userId: string; userData: UpdateUserOrganizationsData }) => {
      return await apiClient.put<APIMessageResponse>(
        `/enterprise/v1/users/${userId}/organizations`,
        {
          body: userData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts-updated'] });
    },
    onError: (error) => {
      return error;
    },
  });
};